/**
 * Dev-auth abstraction (ADR-5).
 *
 * Two separate mechanisms:
 *
 * 1. devAuthEnabled — full MSAL bypass (DEV + VITE_DEV_AUTH=true).
 *    API client sends X-Dev-Role instead of a Bearer token.
 *    Used when no live Entra registration exists.
 *
 * 2. devRoleOverrideEnabled — UI-only role override (any DEV build).
 *    Real MSAL auth still runs; API calls carry real JWT tokens.
 *    The role switcher overrides only what the frontend *displays*
 *    (RequireRole, useCurrentUser, nav items) so you can preview
 *    each role's UI without a separate Azure AD account.
 *
 * Security: both flags fold to false in any production build because
 * import.meta.env.DEV is statically false after `vite build`.
 */

/** The three simulated identity levels. Matches the X-Dev-Role header values. */
export type DevRole = 'external' | 'reviewer' | 'admin';

const DEV_ROLE_KEY = 'dev-auth-role';
const DEV_ROLE_OVERRIDE_KEY = 'dev-role-override';
const DEFAULT_ROLE: DevRole = 'external';

// ---------------------------------------------------------------------------
// Full MSAL bypass (devAuthEnabled)
// ---------------------------------------------------------------------------

/**
 * True only in a Vite dev-server build AND when VITE_DEV_AUTH=true is set.
 * When true, the API client sends X-Dev-Role instead of a Bearer token.
 * Dead code in any production bundle.
 */
export const devAuthEnabled: boolean =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === 'true';

/** Read the bypass role from localStorage (used only when devAuthEnabled). */
export function getDevRole(): DevRole {
  const stored = localStorage.getItem(DEV_ROLE_KEY);
  if (stored === 'external' || stored === 'reviewer' || stored === 'admin') {
    return stored;
  }
  return DEFAULT_ROLE;
}

/** Persist the bypass role (used only when devAuthEnabled). */
export function setDevRole(role: DevRole): void {
  localStorage.setItem(DEV_ROLE_KEY, role);
}

/** Clear the bypass role. Useful for tests. */
export function clearDevRole(): void {
  localStorage.removeItem(DEV_ROLE_KEY);
}

/** True when the bypass role grants reviewer or admin access. */
export function isDevReviewerOrAdmin(): boolean {
  const role = getDevRole();
  return role === 'reviewer' || role === 'admin';
}

// ---------------------------------------------------------------------------
// UI-only role override (devRoleOverrideEnabled)
// ---------------------------------------------------------------------------

/**
 * True in any Vite dev-server build.
 * Enables the role switcher without touching MSAL or API auth.
 * Dead code in any production bundle.
 */
export const devRoleOverrideEnabled: boolean = import.meta.env.DEV;

/**
 * Read the UI role override from localStorage.
 * Returns null when no override is active ("auto" — use the real token role).
 */
export function getDevRoleOverride(): DevRole | null {
  const stored = localStorage.getItem(DEV_ROLE_OVERRIDE_KEY);
  if (stored === 'external' || stored === 'reviewer' || stored === 'admin') {
    return stored;
  }
  return null;
}

/**
 * Set the UI role override. Pass null to clear it (revert to real token role).
 */
export function setDevRoleOverride(role: DevRole | null): void {
  if (role === null) {
    localStorage.removeItem(DEV_ROLE_OVERRIDE_KEY);
  } else {
    localStorage.setItem(DEV_ROLE_OVERRIDE_KEY, role);
  }
}

/** Clear the UI role override. Useful for tests. */
export function clearDevRoleOverride(): void {
  localStorage.removeItem(DEV_ROLE_OVERRIDE_KEY);
}
